import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeToggle } from "../../theme";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Snackbar,
  Alert,
  FormControl,
  RadioGroup,
  Radio,
  FormControlLabel,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MoreVertIcon from "@mui/icons-material/MoreVert";

// Import components from both versions
import ProfileHeader from "./ProfileHeader";
import ProfileTabs from "./ProfileTabs";
import CreatedDivesites from "./createdDivesites";
import SharedDivesSection from "../shared-dives";
import UploadedMedia from "./UploadedMedia";
import DiverCertifications from "./certifications";
import DiverCredentials from "../credentialsAndDivesReport.js";
import { useUnits } from "../../contexts/UnitContext.js";
import ListDiveSiteStubs from "./ListDiveSiteStubs.js";
import DivingCredentialsComponent from "../credentialsAndDivesReport.js";

// Helper component for Tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

export default function Profile() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [loading, setLoading] = useState(false);
  const { currentUser, updateUserProfile, setError, userObjectId } = useAuth();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const { theme } = useThemeToggle();
  
  // Admin functionality from first version
  const [isAdmin, setIsAdmin] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  
  // Tab functionality from second version
  const [currentTab, setCurrentTab] = useState(0);

  // Admin menu handlers
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const navigateToAdmin = () => {
    handleMenuClose();
    navigate("/admin");
  };

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser) {
        try {
          const token = await currentUser.getIdTokenResult(true);
          setIsAdmin(!!token.claims.isAdmin);
        } catch (error) {
          console.error("Error checking admin status:", error);
        }
      }
    };
    checkAdminStatus();
  }, [currentUser]);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  // Destructure all unit preferences including divelogPreference from first version
  const {
    unitSystem,
    timeFormat,
    divelogPreference, // From first version
    updateUnitPreference,
    updateTimeFormat,
    updateDivelogPreference, // From first version
  } = useUnits();

  const handleUnitChange = async (event) => {
    const newUnitSystem = event.target.value;
    const success = await updateUnitPreference(newUnitSystem);

    if (success) {
      setSnackbarMessage("Unit preference updated successfully!");
      setSnackbarSeverity("success");
    } else {
      setSnackbarMessage("Failed to update unit preference");
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  const handleTimeFormatChange = async (event) => {
    const newTimeFormat = event.target.value;
    const success = await updateTimeFormat(newTimeFormat);

    if (success) {
      setSnackbarMessage("Time format updated successfully!");
      setSnackbarSeverity("success");
    } else {
      setSnackbarMessage("Failed to update time format");
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);
  };

  // Handler for Dive Log Format preference change (from first version)
  const handleDivelogFormatChange = async (event) => {
    const newDivelogFormat = event.target.value;
    if (updateDivelogPreference) {
      const success = await updateDivelogPreference(newDivelogFormat);
      if (success) {
        setSnackbarMessage("Dive log format preference updated successfully!");
        setSnackbarSeverity("success");
      } else {
        setSnackbarMessage("Failed to update dive log format preference.");
        setSnackbarSeverity("error");
      }
      setSnackbarOpen(true);
    } else {
      console.error("updateDivelogPreference function is not available from useUnits context.");
      setSnackbarMessage("Error: Dive log preference update feature not configured.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const Spacer = ({ height = '16px' }) => <div style={{ height }} />;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await updateUserProfile(currentUser, {
        displayName:
          username.trim() !== "" ? username : currentUser.displayName,
      });

      const token = await currentUser.getIdToken();
      const response = await fetch(
        `/api/diver/${userObjectId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ displayName: username }),
        }
      );

      if (response.ok) {
        setSnackbarMessage("Profile updated successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        const errorData = await response.json();
        setError(
          `Failed to update profile in database: ${
            errorData.message || response.statusText
          }`
        );
        setSnackbarMessage(
          `Failed to update profile in database: ${
            errorData.message || response.statusText
          }`
        );
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      setError(`Failed to update profile: ${error.message}`);
      setSnackbarMessage(`Failed to update profile: ${error.message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      console.error("Profile update error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handler for tab change (from second version)
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
      }}
    >
      {/* Use ProfileHeader if available, otherwise fallback to original AppBar */}
      {ProfileHeader ? (
        <ProfileHeader />
      ) : (
        <AppBar position="static" color="primary">
          <Toolbar>
            <IconButton edge="start" color="inherit" aria-label="profile">
              <AccountCircleIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Profile
            </Typography>
            {isAdmin && (
              <>
                <IconButton
                  color="inherit"
                  onClick={handleMenuOpen}
                  aria-label="more options"
                  aria-controls="profile-menu"
                  aria-haspopup="true"
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="profile-menu"
                  anchorEl={menuAnchorEl}
                  keepMounted
                  open={Boolean(menuAnchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={navigateToAdmin}>
                    <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                    Admin Dashboard
                  </MenuItem>
                </Menu>
              </>
            )}
          </Toolbar>
        </AppBar>
      )}

      {/* Tabs for navigation (if ProfileTabs component exists) */}
      {ProfileTabs && (
        <Container maxWidth="md" sx={{ px: { xs: 0, sm: 2, md: 3 } }}>
          <ProfileTabs tabValue={currentTab} handleTabChange={handleTabChange} />
        </Container>
      )}

      {/* Main Content Area */}
      <Container
        maxWidth={ProfileTabs ? "md" : "sm"}
        sx={{ py: 3, flexGrow: 1, px: { xs: 0, sm: 2, md: 3 } }}
      >
        {/* Admin status card (from first version) */}
        {isAdmin && (
          <Card sx={{ mb: 2, backgroundColor: theme.palette.primary.light }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <AdminPanelSettingsIcon sx={{ mr: 1 }} />
                <Typography variant="body1" color="white">
                  You have administrator privileges
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="secondary"
                sx={{ mt: 1 }}
                onClick={navigateToAdmin}
              >
                Go to Admin Dashboard
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Conditional rendering based on whether tabs are being used */}
        {ProfileTabs ? (
          <>
            {/* Tab 0: Account Details */}
            <TabPanel value={currentTab} index={0}>
              <Card
                sx={{
                  mb: 2,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    color={theme.palette.text.primary}
                  >
                    Display Name
                  </Typography>
                  <TextField
                    fullWidth
                    id="username"
                    label="Enter your preferred display name"
                    variant="outlined"
                    defaultValue={currentUser?.displayName || ""}
                    onChange={(e) => setUsername(e.target.value)}
                    margin="normal"
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading}
                    fullWidth
                    sx={{ mt: 2, color: theme.palette.primary.contrastText }}
                    onClick={handleFormSubmit}
                  >
                    {loading ? "Updating..." : "Update"}
                  </Button>
                </CardContent>
              </Card>
              {DivingCredentialsComponent && <DivingCredentialsComponent />}
            </TabPanel>

            {/* Tab 1: Diver Certifications */}
            <TabPanel value={currentTab} index={1}>
              {DiverCertifications && <DiverCertifications />}
            </TabPanel>

            {/* Tab 2: Created Divesites */}
            <TabPanel value={currentTab} index={2}>
              <CreatedDivesites />
              {ListDiveSiteStubs && <ListDiveSiteStubs />}
            </TabPanel>

            {/* Tab 3: Shared Dives Section */}
            <TabPanel value={currentTab} index={3}>
              <SharedDivesSection />
            </TabPanel>

            {/* Tab 4: Uploaded Media */}
            <TabPanel value={currentTab} index={4}>
              <UploadedMedia size="medium" />
            </TabPanel>

            {/* Tab 5: Display Preferences */}
            <TabPanel value={currentTab} index={5}>
              <Card
                sx={{
                  mb: 2,
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                }}
              >
                <CardContent>
                  <Typography
                    variant="h6"
                    gutterBottom
                    color={theme.palette.text.primary}
                  >
                    Display & Log Preferences
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Typography variant="subtitle1" gutterBottom>
                    Unit System
                  </Typography>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      aria-label="unit system"
                      name="unit-system"
                      value={unitSystem || "english"}
                      onChange={handleUnitChange}
                    >
                      <FormControlLabel
                        value="english"
                        control={<Radio />}
                        label="English (ft, °F, psi)"
                      />
                      <FormControlLabel
                        value="metric"
                        control={<Radio />}
                        label="Metric (m, °C, bar)"
                      />
                    </RadioGroup>
                  </FormControl>

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Time Format
                  </Typography>
                  <FormControl component="fieldset" sx={{ mb: 2 }}>
                    <RadioGroup
                      row
                      aria-label="time format"
                      name="time-format"
                      value={timeFormat || "12h"}
                      onChange={handleTimeFormatChange}
                    >
                      <FormControlLabel
                        value="12h"
                        control={<Radio />}
                        label="12-hour (AM/PM)"
                      />
                      <FormControlLabel
                        value="24h"
                        control={<Radio />}
                        label="24-hour"
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Dive Log Format Preference Section (from first version) */}
                  {updateDivelogPreference && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle1" gutterBottom>
                        Dive Log Format
                      </Typography>
                      <FormControl component="fieldset">
                        <RadioGroup
                          row
                          aria-label="dive log format"
                          name="dive-log-format"
                          value={divelogPreference || 'short'}
                          onChange={handleDivelogFormatChange}
                        >
                          <FormControlLabel
                            value="short"
                            control={<Radio />}
                            label="Short"
                          />
                          <FormControlLabel
                            value="PADI"
                            control={<Radio />}
                            label="PADI"
                          />
                          <FormControlLabel
                            value="NAUI"
                            control={<Radio />}
                            label="NAUI"
                          />
                        </RadioGroup>
                      </FormControl>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabPanel>
          </>
        ) : (
          /* Original single-page layout (from first version) */
          <>
            <Card
              sx={{
                mb: 2,
                backgroundColor: theme.palette.paper,
                color: theme.palette.text.primary,
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  color={theme.palette.text.primary}
                >
                  Display Name
                </Typography>
                <TextField
                  fullWidth
                  id="username"
                  label="Enter your preferred display name"
                  variant="outlined"
                  defaultValue={currentUser?.displayName || ""}
                  onChange={(e) => setUsername(e.target.value)}
                  margin="normal"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  fullWidth
                  sx={{ mt: 2, color: theme.palette.primary.contrastText }}
                  onClick={handleFormSubmit}
                >
                  {loading ? "Updating..." : "Update Display Name"}
                </Button>
              </CardContent>
            </Card>
            
            {ListDiveSiteStubs && <ListDiveSiteStubs />}
            <CreatedDivesites />
            <SharedDivesSection />
            <Spacer height="16px" />
            <UploadedMedia size="medium" />

            <Card sx={{ mb: 2, backgroundColor: theme.palette.paper, color: theme.palette.text.primary }}>
              <CardContent>
                <Typography variant="h6" gutterBottom color={theme.palette.text.primary}>
                  Display & Log Preferences
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" gutterBottom>
                  Unit System
                </Typography>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    aria-label="unit system"
                    name="unit-system"
                    value={unitSystem || "english"}
                    onChange={handleUnitChange}
                  >
                    <FormControlLabel
                      value="english"
                      control={<Radio />}
                      label="English (ft, °F, psi)"
                    />
                    <FormControlLabel
                      value="metric"
                      control={<Radio />}
                      label="Metric (m, °C, bar)"
                    />
                  </RadioGroup>
                </FormControl>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Time Format
                </Typography>
                <FormControl component="fieldset" sx={{ mb: 2 }}>
                  <RadioGroup
                    row
                    aria-label="time format"
                    name="time-format"
                    value={timeFormat || "12h"}
                    onChange={handleTimeFormatChange}
                  >
                    <FormControlLabel
                      value="12h"
                      control={<Radio />}
                      label="12-hour (AM/PM)"
                    />
                    <FormControlLabel
                      value="24h"
                      control={<Radio />}
                      label="24-hour"
                    />
                  </RadioGroup>
                </FormControl>

                {/* Dive Log Format Preference Section */}
                {updateDivelogPreference && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" gutterBottom>
                      Dive Log Format
                    </Typography>
                    <FormControl component="fieldset">
                      <RadioGroup
                        row
                        aria-label="dive log format"
                        name="dive-log-format"
                        value={divelogPreference || 'short'}
                        onChange={handleDivelogFormatChange}
                      >
                        <FormControlLabel
                          value="short"
                          control={<Radio />}
                          label="Short"
                        />
                        <FormControlLabel
                          value="PADI"
                          control={<Radio />}
                          label="PADI"
                        />
                        <FormControlLabel
                          value="NAUI"
                          control={<Radio />}
                          label="NAUI"
                        />
                      </RadioGroup>
                    </FormControl>
                  </>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Container>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            color: theme.palette.text.primary,
            backgroundColor: snackbarSeverity === 'success' 
              ? theme.palette.success.light 
              : theme.palette.error.light,
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
